import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItemButton,
  Typography,
} from '@mui/material';

import { getCurrentUserProfile } from '../../actions/userProfile';
import SpeedButtons from '../controls/SpeedButtons';
import LoadingSpinner from '../layout/LoadingSpinner';
import CapacityStat from './CapacityStat';

import { ActPriceList } from '../controls/ActPriceList';
import { LoggedInUsers } from '../controls/LoggedInUsers';
import { TodaysLessonsItem } from '../controls/TodaysLessonsItem';
import { TodaysLessonPopup } from '../controls/TodaysLessonsPopup';

import {
  getActualPriceList,
  getLoggedInUsers,
  getTodaysLessons,
} from '../../api/dashboardAPI';

const DashboardCard = ({ title, children, height }) => {
  return (
    <Card sx={{ borderRadius: 3, height: '100%' }}>
      {title && (
        <Box sx={{ px: 2, pt: 2 }}>
          <Typography variant='h6' fontWeight={700}>
            {title}
          </Typography>
        </Box>
      )}

      <CardContent
        sx={{
          height,
          overflow: height ? 'auto' : 'visible',
        }}
      >
        {children}
      </CardContent>
    </Card>
  );
};

const Dashboard = ({
  getCurrentUserProfile,
  userProfile: { profile, loading },
}) => {
  const [todaysLessons, setTodaysLessons] = useState([]);
  const [actPricelist, setActPricelist] = useState([]);
  const [loginUsers, setLoginUsers] = useState([]);

  const [todaysLessonPopupVisible, setTodaysLessonPopupVisible] =
    useState(false);

  const [todaysLessonActualLesson, setTodaysLessonActualLesson] = useState({});

  useEffect(() => {
    getCurrentUserProfile();
  }, [getCurrentUserProfile]);

  useEffect(() => {
    async function loadDashboardData() {
      const [lessons, prices, users] = await Promise.all([
        getTodaysLessons(),
        getActualPriceList(),
        getLoggedInUsers(),
      ]);

      setTodaysLessons(
        (lessons ?? []).sort(
          (a, b) => new Date(a.date_start) - new Date(b.date_start),
        ),
      );

      setActPricelist(prices ?? []);
      setLoginUsers(users ?? []);
    }

    loadDashboardData();
  }, []);

  const handleTodaysLessonClick = (item) => {
    setTodaysLessonActualLesson(item);
    setTodaysLessonPopupVisible(true);
  };

  const handleCloseTodaysLessonPopup = () => {
    setTodaysLessonPopupVisible(false);
    setTodaysLessonActualLesson({});
  };

  if (loading && profile === null) {
    return <LoadingSpinner />;
  }

  return (
    <Fragment>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DashboardCard>
              <SpeedButtons />
            </DashboardCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <DashboardCard title='Mai óráim' height={400}>
              <List disablePadding>
                {todaysLessons.map((lesson, index) => (
                  <ListItemButton
                    key={lesson.id ?? `${lesson.date_start}-${index}`}
                    onClick={() => handleTodaysLessonClick(lesson)}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                    }}
                  >
                    <TodaysLessonsItem data={lesson} />
                  </ListItemButton>
                ))}
              </List>
            </DashboardCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <DashboardCard title='Aktuális árlista'>
              <List disablePadding>
                {actPricelist.map((item, index) => (
                  <Box key={item.id ?? index} sx={{ mb: 1 }}>
                    <ActPriceList data={item} />
                  </Box>
                ))}
              </List>
            </DashboardCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <DashboardCard title='Belépett felhasználók' height={250}>
              <List disablePadding>
                {loginUsers.map((item, index) => (
                  <Box key={item.id ?? item.type ?? index} sx={{ mb: 1 }}>
                    <LoggedInUsers data={item} />
                  </Box>
                ))}
              </List>
            </DashboardCard>
          </Grid>

          <Grid item xs={12} md={12}>
            <DashboardCard title='Statisztika'>
              <CapacityStat tanev='2025/2026' oktatoSelect={false} />
            </DashboardCard>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={todaysLessonPopupVisible}
        onClose={handleCloseTodaysLessonPopup}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle>Jelenlét</DialogTitle>

        <DialogContent dividers>
          <TodaysLessonPopup popupItems={todaysLessonActualLesson} />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseTodaysLessonPopup}>Bezár</Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

Dashboard.propTypes = {
  getCurrentUserProfile: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  userProfile: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  userProfile: state.userProfile,
});

export default connect(mapStateToProps, {
  getCurrentUserProfile,
})(Dashboard);
